<template>
  <v-card
    class="action-header-card"
    :class="{ archived: is_archived, ...card_classes }"
    :role="interactive ? 'button' : null"
    :tabindex="interactive ? 0 : -1"
    :aria-label="aria_label || 'Edit item'"
    @click="interactive ? $emit('click') : null"
    @keydown.enter="interactive ? $emit('click') : null"
    @keydown.space.prevent="interactive ? $emit('click') : null"
  >
    <div class="card-header">
      <div
        v-if="header_accent_color"
        class="header-accent"
        :style="{ backgroundColor: header_accent_color }"
        role="presentation"
        :aria-label="`Accent color: ${header_accent_color}`"
      ></div>

      <div class="header-content pa-4">
        <v-row align="center" justify="space-between" no-gutters>
          <v-col class="header-text-section">
            <div class="header-text-container">
              <h3 class="header-title">{{ computed_header_text }}</h3>
              <div v-if="subheader_text || $slots.subheader" class="header-subheader">
                <!-- @slot Custom content for the subheader area -->
                <slot name="subheader">
                  <span v-if="subheader_text">{{ subheader_text }}</span>
                </slot>
              </div>
            </div>
          </v-col>

          <v-col cols="auto" class="ml-0 flex-shrink-0">
            <v-menu bottom left>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop
                  class="action-menu-btn"
                  :aria-label="menu_aria_label || 'Actions menu'"
                >
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list class="py-0" dense>
                <template v-for="(item, index) in menu_items">
                  <v-divider v-if="item.divider" :key="`divider-${index}`" />

                  <template v-else>
                    <v-list-item
                      v-if="!item.org_admins_only || $rc.$state.isOrganizationAdmin()"
                      :key="index"
                      @click="$emit('menu-action', item.action, item)"
                    >
                      <v-list-item-icon v-if="item.icon">
                        <v-icon small>{{ item.icon }}</v-icon>
                      </v-list-item-icon>

                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>

                    <m-restricted-menu-list-item
                      v-else
                      :key="`item-${index}`"
                      :text="item.text"
                      :icon="item.icon"
                      tooltip_text="Only organization admins can perform this action"
                    />
                  </template>
                </template>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </div>
    </div>

    <v-card-text class="pa-0">
      <!-- @slot Main card content area -->
      <slot></slot>
    </v-card-text>
  </v-card>
</template>

<script>
/**
 * Reusable card component with action header and menu
 * @vue-component
 * @example
 * <card-with-action-header
 *   header_text="Card Item"
 *   header_accent_color="#1976d2"
 *   :menu_items="menuItems"
 *   :interactive="true"
 *   @click="handleCardClick"
 *   @menu-action="handleMenuAction"
 * >
 *   <template #subheader>
 *     <v-chip small>Active</v-chip>
 *   </template>
 *   <div>Card content goes here</div>
 * </card-with-action-header>
 */
export default {
  name: 'CardWithActionHeader',

  props: {
    /**
     * Main header text displayed in the card
     */
    header_text: {
      type: String,
      required: true,
    },
    /**
     * Optional subheader text (can be overridden by subheader slot)
     */
    subheader_text: {
      type: String,
      default: '',
    },
    /**
     * Background color for the header accent bar
     * @example '#1976d2', 'rgb(25, 118, 210)', 'blue'
     */
    header_accent_color: {
      type: String,
      default: '',
    },
    /**
     * Array of menu items for the action menu
     * If org_admins_only is set to true, restricted menu list item component is shown instead
     *
     * @example
     * [
     *   { text: 'Edit', icon: 'mdi-pencil', action: 'edit' },
     *   { divider: true },
     *   { text: 'Delete', icon: 'mdi-delete', action: 'delete', org_admins_only: true }
     * ]
     */
    menu_items: {
      type: Array,
      default: () => [],
    },
    /**
     * ARIA label for the menu button
     */
    menu_aria_label: {
      type: String,
      default: '',
    },
    /**
     * Whether the card is clickable
     */
    interactive: {
      type: Boolean,
      default: false,
    },
    /**
     * ARIA label for the entire card
     */
    aria_label: {
      type: String,
      default: '',
    },
    /**
     * Additional CSS classes to apply to the card
     */
    card_classes: {
      type: [String, Array, Object],
      default: '',
    },
    /**
     * Whether the card's item should be visually indicated as archived
     * Will also append [ARCHIVED] to the header text
     * Takes precedent over is_draft prop
     */
    is_archived: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether [DRAFT] should be appended to the header text
     */
    is_draft: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    /**
     * Computed header text to ensure it's a string
     */
    computed_header_text() {
      if (this.is_archived) {
        return `${this.header_text} [ARCHIVED]`;
      }
      if (this.is_draft) {
        return `${this.header_text} [DRAFT]`;
      }
      return this.header_text || '';
    },
  },

  /**
   * @event click - Emitted when interactive card is clicked
   * @event menu-action - Emitted when menu item is selected (action, item)
   */

  emits: ['click', 'menu-action'],
};
</script>

<style lang="scss" scoped>
.action-header-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.08);

  &[role='button'] {
    cursor: pointer;

    &:hover {
      transform: translateY(-0.25rem);
      box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.15);
    }

    &:focus {
      outline: 2px solid var(--v-primary-base);
      outline-offset: 2px;
    }
  }
}

.card-header {
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  .header-accent {
    height: 0.25rem;
    width: 100%;
  }

  .header-content {
    padding: 1rem 1.25rem;
  }
}

.header-text-container {
  .header-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.87);
    margin-bottom: 0.5rem;
    line-height: 1.3;
    margin: 0;
  }

  .header-subheader {
    margin-top: 0.5rem;
  }
}

.action-menu-btn {
  background-color: rgba(0, 0, 0, 0.04);
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }
}

.archived {
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
}
</style>
